import { useState } from "react";
import { PaperBillInfoProps } from "../types/PaperBillInfo.types";
import {
  CreateInsuranceTransactionDto,
  ProcessedPaperBillingDto,
} from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { transactionMethods } from "../../../../Kasse/v2/types/kasse.types";
import { BillingTypeEnum } from "../../../Base/types/insuranceBills.types";

export function usePaperBillInfo(props: PaperBillInfoProps) {
  const {
    onRowClick,
    account,
    userName,
    userId,
    onRxDelete,
    onValidationFilter,
    onBillsSaved,
    onRemoveAll,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRx, setSelectedRx] = useState<any | null>(null);
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSingleRxDialog, setOpenSingleRxDialog] = useState(false);
  const [rxNumber, setRxNumberInput] = useState("");
  const [rxNumberError, setRxNumberError] = useState("");

  const [tableVisible, setTableVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bills, setBills] = useState<ProcessedPaperBillingDto[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const TransactionStore = useStore(TransactionsContext);
  const {
    PaperBillingAllInfo,
    createTransaction,
    getSingleRxForIndirectBillsForInsurance,
  } = TransactionStore;

  // Kebab Menu
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    rx: any,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRx(rx);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Delete
  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };
  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedRx(null);
  };
  const handleDeleteConfirm = () => {
    if (!selectedRx) return;

    setBills((prev) =>
      prev
        .map((bill) => {
          if (bill.rxs.some((rx) => rx.id === selectedRx.id)) {
            const updatedRxData = bill.rxs.filter(
              (rx) => rx.id !== selectedRx.id,
            );
            const updatedTotalPrice = updatedRxData.reduce(
              (total, rx) => total + (rx.rxPrice.netValue || 0),
              0,
            );

            return {
              ...bill,
              rxs: updatedRxData,
              totalPrice: updatedTotalPrice,
            };
          }
          return bill;
        })
        .filter((bill) => bill.rxs.length > 0),
    );

    notificationStore.showMessage(
      "Rezept erfolgreich aus der Abrechnung entfernt",
      "success",
    );
    onRxDelete(selectedRx.id);

    setSelectedRx(null);
    setOpenDeleteDialog(false);
  };

  // Add Single Rx
  const handleAddSingleRx = async (rxNumber: string) => {
    if (!rxNumber.trim()) {
      setRxNumberError("Bitte geben Sie eine Rezept-Nummer ein");
      return;
    }

    try {
      const rxInfo = await getSingleRxForIndirectBillsForInsurance(
        rxNumber.trim(),
      );
      if (rxInfo && rxInfo.rx) {
        const newRx = rxInfo.rx;
        // Check if the RX already exists
        const rxExists = bills.some((bill) =>
          bill.rxs.some((rx) => rx.id === newRx.id),
        );
        if (rxExists) {
          notificationStore.showMessage(
            "Dieses Rezept ist bereits auf der Liste",
            "warning",
          );
          return;
        }

        const payerIK = newRx.payer?.payerId;
        if (!payerIK) {
          notificationStore.showMessage(
            "Payer IK not found for the Rx",
            "error",
          );
          return;
        }

        // Update bills
        setBills((prev) => {
          const newBill = {
            payer: newRx.insurancePayer,
            rxs: [newRx],
            totalPrice: newRx.rxPrice?.netValue || newRx.price || 0,
          };
          return [...prev, newBill];
        });

        notificationStore.showMessage(
          "Rezept erfolgreich hinzugefügt",
          "success",
        );
      } else {
        notificationStore.showMessage("Rezept nicht gefunden", "error");
      }
    } catch (error) {
      console.error("Error fetching single RX:", error);
      // The API explains why an Rx cannot be billed here (e.g. Privatrezept) — keep that
      // message instead of flattening everything to "not found".
      const serverMessage = (error as any)?.response?.data?.message;
      notificationStore.showMessage(
        typeof serverMessage === "string" && serverMessage
          ? serverMessage
          : "Rezept nicht gefunden",
        "error",
      );
    }
  };

  // Load All
  const handleLoadAll = async () => {
    setIsLoading(true);
    setTableVisible(true);
    try {
      const fetchedBills = await PaperBillingAllInfo(account);
      setBills(fetchedBills);
    } catch (error) {
      console.error("Error fetching Bills:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save All
  const handleSave = async () => {
    if (bills.length === 0) {
      notificationStore.showMessage("Keine Rechnungen zum Speichern", "warning");
      return;
    }
    try {
      setIsSaving(true);
      const createdIds: string[] = [];
      for (const bill of bills) {
        const { rxs, payer, totalPrice } = bill;
        const payerIK = payer.ik;

        const totalPriceFormatted = totalPrice.toFixed(2);
        const rxsIds = rxs
          .map((rx) => rx.id)
          .filter((id): id is string => !!id);

        const dataSubmission: CreateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: account,
          amount: Number(totalPriceFormatted),
          userId,
          method: transactionMethods.VERSICHERUNG,
          rxIds: rxsIds,
          notes: notes[payerIK] || "", // or store in local notes[payerIK] if you want
          paperCenterId: "",
          dataCenterId: "",
          tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
          billType: BillingTypeEnum.PAPER,
        };

        const created = await createTransaction(dataSubmission);
        createdIds.push(created.id);

        // Remove from local state (since it’s “saved”)
        setBills((prev) => prev.filter((b) => b.payer.ik !== payerIK));
      }
      notificationStore.showMessage(
        "All transactions created successfully",
        "success",
      );
      onBillsSaved(createdIds);
      // handleFullScreenDialogClose();
    } catch (error) {
      console.error("Fehler beim Erstellen der Transaktionen", error);
      notificationStore.showMessage("Fehler beim Erstellen der Transaktionen", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Validation Filter
  const handleValidationFilter = () => {
    const updatedBills = bills
      .map((bill) => {
        const filteredRxs = bill.rxs.filter((rx) => {
          const validations = rx.formvalidation;
          return (
            validations &&
            Object.values(validations).every((v) => v.validity === "Valid")
          );
        });
        const updatedTotalPrice = filteredRxs.reduce(
          (sum: any, rx) => sum + (rx.rxPrice.netValue || 0),
          0,
        );

        return {
          ...bill,
          data: {
            ...bill,
            rxs: filteredRxs,
            totalPrice: updatedTotalPrice,
          },
        };
      })
      .filter((b) => b.data.rxs.length > 0);

    setBills(updatedBills);
    const remainingRxs = updatedBills.flatMap((b) => b.data.rxs);
    onValidationFilter?.(remainingRxs);
  };

  // Remove All bills for a specific payer
  const handleRemoveAll = (billIndex: number) => {
    const billToRemove = bills[billIndex];
    if (!billToRemove) return;

    setBills((prevBills) =>
      prevBills.filter((_, index) => index !== billIndex),
    );

    // Pass the removed bill's rxs to parent so it can clear selection if needed
    onRemoveAll?.(billToRemove);
  };

  return {
    // states
    anchorEl,
    selectedRxId,
    openDeleteDialog,
    openSingleRxDialog,
    rxNumberInput: rxNumber,
    rxNumberError,
    tableVisible,
    isLoading,
    bills,
    notes,
    isSaving,

    // setters
    setSelectedRxId,
    setRxNumberInput,
    setOpenSingleRxDialog,
    setNotes,

    // handlers
    handleLoadAll,
    handleAddSingleRx,
    handleValidationFilter,
    handleSave,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleDeleteClick,
    handleMenuOpen,
    handleMenuClose,
    handleRemoveAll,

    // from props
    onRowClick,
    userName,
  };
}
